// src/contexts/MessageCountContext.js
import React, {
  createContext, useContext, useEffect, useRef,
  useCallback, useMemo
} from 'react';
import { useBadgeSessionKey, useBadgeQuery } from './BadgeResourceContext';
import { useScopedBadgeState, useBadgeRequestGuard } from '../hooks/useScopedBadgeState';
import { useLocation } from 'react-router-dom';
import { getAdminConciergePendingCount } from '../api/concierge';
import { getSocket, resetSocket } from '../lib/socket';
import { SOCKET_EVENTS } from '../lib/socketEvents';
import { useAuth } from '../contexts/AuthContext';
import { useArtistAuth } from '../contexts/ArtistAuthContext';
import { resolveAdminPermission } from '../constants/adminPermissionCatalog';
import { getActiveChatContext, isMatchingActiveChatContext } from '../utils/activeChatContext';
import {
  getAdminPersonalUnreadCount,
} from '../api/adminChat';
import {
  getAdminGroupChatUnreadCount,
} from '../api/adminGroupChat';
import {
  getArtistGroupChatUnreadCount,
  getArtistPersonalUnreadCount,
} from '../api/artistChat';
import {
  getAdminEventChatUnreadCounts,
  getArtistEventChatUnreadCounts,
  listArtistEventChatAvailableEvents,
} from '../api/eventChat';
import { getChatMentionUnreadSummary } from '../api/chatMentions';

const assertUnreadRows = (rows, idKey) => {
  if (!Array.isArray(rows) || rows.some((row) =>
    !row?.[idKey] || row.unread_count == null ||
    !Number.isSafeInteger(Number(row.unread_count)) || Number(row.unread_count) < 0
  )) throw new Error('Invalid unread counts');
};

const C = { ADMIN: 'admin', ARTIST: 'artist' };
const UNREAD_OVERRIDE_TTL_MS = 1500;
// §3.7/§3.8: 対象外チャットの socket 受信 → チャンネル別 unread-count 再取得のデバウンス。
const BADGE_REFRESH_DEBOUNCE_MS = 250;

const isAnyChatOpenPath = (path = '') =>
  path.startsWith('/admin/workspaces/communications/artist-chat') ||
  path.startsWith('/admin/workspaces/event-operations/event-chat') ||
  path.startsWith('/admin/workspaces/communications/group-chat') ||
  path.startsWith('/artist/workspaces/communications/chat') ||
  path.startsWith('/artist/workspaces/event-operations/event-chat');

const firstPayloadId = (payload = {}, keys = []) => {
  for (const key of keys) {
    const value = payload?.[key];
    const normalized = String(value == null ? '' : value).trim();
    if (normalized) return normalized;
  }
  return '';
};

const isPayloadForActiveChat = (channel, payload = {}) => {
  const active = getActiveChatContext();
  if (!active || active.channel !== channel || !active.scope) return false;

  const incoming = { scope: active.scope, channel, topicId: firstPayloadId(payload, ['topicId', 'topic_id']) || String(payload?.topic?.id || '') };
  if (channel === 'personal') {
    incoming.artistId = firstPayloadId(payload, [
      'artistId',
      'artist_id',
      'targetArtistId',
      'target_artist_id',
    ]);
  } else if (channel === 'group') {
    incoming.threadId = firstPayloadId(payload, ['threadId', 'thread_id', 'groupThreadId', 'group_thread_id']);
  } else if (channel === 'event') {
    incoming.eventId = firstPayloadId(payload, ['eventId', 'event_id', 'eventUuid', 'event_uuid']);
    incoming.threadId = firstPayloadId(payload, ['threadId', 'thread_id', 'chatThreadId', 'chat_thread_id']);
  }

  return isMatchingActiveChatContext(active, incoming);
};

const dispatchPeerNotification = (detail) => {
  try {
    window.dispatchEvent(new CustomEvent('peer:notification', { detail }));
  } catch (_) {
    // noop
  }
};

const noop = () => {};
const asyncNoop = async () => {};
const defaultMessageCountContext = {
  // 個別メッセージ（合計）
  artistMessageCount: 0,
  setArtistMessageCount: noop,
  artistPersonalMessageCount: 0,
  artistGroupChatMessageCount: 0,
  artistGroupUnreadMap: {},
  adminGroupChatMessageCount: 0,
  adminGroupUnreadMap: {},
  adminMessageCount: 0,
  setAdminMessageCount: noop,

  // イベントチャット（合計バッジ）
  eventChatMessageCount: 0,
  setEventChatMessageCount: noop,

  // イベントチャット（役割別の実値＆マップ）
  eventChatUnreadAdmin: 0,
  eventChatUnreadArtist: 0,
  eventUnreadMapAdmin: {},
  eventUnreadMapArtist: {},

  // 管理者バッジトーン
  adminArtistChatTone: null,
  setAdminArtistChatTone: noop,
  adminEventChatTone: null,
  setAdminEventChatTone: noop,
  adminGroupChatTone: null,
  setAdminGroupChatTone: noop,

  // 未読メンション在否（event/group）＝バッジ黄色（メンション色）判定
  eventChatHasMention: false,
  groupChatHasMention: false,
  refreshMentionSummary: asyncNoop,

  // コンシェルジュ（管理者）未処理件数
  conciergePendingCount: 0,
  refreshConciergePendingCount: asyncNoop,

  // 取得・更新系
  refreshEventChatUnread: asyncNoop,
  getEventUnreadCount: () => 0,
  markEventAsRead: noop,
  markAdminGroupThreadAsRead: noop,
  markArtistGroupThreadAsRead: noop,
  optimisticallyClearAdminPersonalUnread: noop,

  // 追加公開（個人メッセージ）
  refreshArtistPersonalUnread: asyncNoop,
  refreshArtistGroupChatUnread: asyncNoop,
  refreshAdminGroupChatUnread: asyncNoop,
  refreshAdminPersonalUnread: asyncNoop,
};

const MessageCountContext = createContext(defaultMessageCountContext);

const sumUnreadMap = (map = {}) =>
  Object.values(map || {}).reduce((sum, value) => sum + Number(value || 0), 0);

const pruneUnreadOverrides = (overrideRef, now = Date.now()) => {
  if (!overrideRef?.current) return new Map();
  for (const [key, expiresAt] of overrideRef.current.entries()) {
    if (!Number.isFinite(expiresAt) || expiresAt <= now) {
      overrideRef.current.delete(key);
    }
  }
  return overrideRef.current;
};

const applyUnreadOverrideMap = (baseMap = {}, overrideRef, now = Date.now()) => {
  const next = { ...(baseMap || {}) };
  const overrides = pruneUnreadOverrides(overrideRef, now);
  overrides.forEach((expiresAt, key) => {
    if (Number.isFinite(expiresAt) && expiresAt > now) {
      next[key] = 0;
    }
  });
  return next;
};

export const MessageCountProvider = ({ children }) => {
  const badgeScope = useBadgeSessionKey();
  const { pathname } = useLocation();
  const beginRequest = useBadgeRequestGuard(badgeScope);
  /* 個別メッセージ未読 */
  const [artistPersonalMessageCount, setArtistPersonalMessageCount] = useScopedBadgeState(badgeScope, 0); // アーティスト個別チャット
  const [artistGroupChatMessageCount, setArtistGroupChatMessageCount] = useScopedBadgeState(badgeScope, 0); // アーティストグループチャット
  const [adminGroupChatMessageCount, setAdminGroupChatMessageCount] = useScopedBadgeState(badgeScope, 0); // 管理者グループチャット
  const [adminMessageCount,  setAdminMessageCount ] = useScopedBadgeState(badgeScope, 0); // 管理者側用（Sidebarのバッジ）

  /* イベントチャット未読（役割別に「イベントごとのマップ」と「合計」を保持） */
  const [eventUnreadMapAdmin,  setEventUnreadMapAdmin ]  = useScopedBadgeState(badgeScope, {}); // { [eventId]: count }
  const [eventUnreadMapArtist, setEventUnreadMapArtist ] = useScopedBadgeState(badgeScope, {}); // { [eventId]: count }
  const [eventChatUnreadAdmin,  setEventChatUnreadAdmin ]  = useScopedBadgeState(badgeScope, 0);
  const [eventChatUnreadArtist, setEventChatUnreadArtist ] = useScopedBadgeState(badgeScope, 0);
  const [adminArtistChatTone, setAdminArtistChatTone] = useScopedBadgeState(badgeScope, null);
  const [adminEventChatTone, setAdminEventChatTone] = useScopedBadgeState(badgeScope, null);
  const [adminGroupChatTone, setAdminGroupChatTone] = useScopedBadgeState(badgeScope, null);
  const mentionScope = `${badgeScope}:${pathname.startsWith('/artist') ? 'artist' : 'admin'}`;
  const [eventChatHasMention, setEventChatHasMention] = useScopedBadgeState(mentionScope, false);
  const [groupChatHasMention, setGroupChatHasMention] = useScopedBadgeState(mentionScope, false);
  const [artistGroupUnreadMap, setArtistGroupUnreadMap] = useScopedBadgeState(badgeScope, {});
  const [adminGroupUnreadMap, setAdminGroupUnreadMap] = useScopedBadgeState(badgeScope, {});

  const artistMessageCount = useMemo(
    () => Number(artistPersonalMessageCount || 0) + Number(artistGroupChatMessageCount || 0),
    [artistPersonalMessageCount, artistGroupChatMessageCount]
  );

  // 後方互換：既存の setArtistMessageCount は「個別チャット未読」を操作する
  const setArtistMessageCount = useCallback((next) => {
    setArtistPersonalMessageCount((prev) => {
      const computed = typeof next === 'function' ? next(prev) : next;
      const num = Number(computed || 0);
      return Number.isFinite(num) && num >= 0 ? num : 0;
    });
  }, [badgeScope]);

  const { user: adminUser, fetchSession, checking } = useAuth();
  const { artistUser }      = useArtistAuth();
  const isTaskOnlyAdmin =
    adminUser?.userType === 'admin' && String(adminUser?.role || '').toLowerCase() === 'task_only';
  const adminPermissionOptions = useMemo(() => ({
    role: adminUser?.role,
    permissions: adminUser?.permissions,
    isExternalAccount: adminUser?.is_external_account === true,
  }), [adminUser?.is_external_account, adminUser?.permissions, adminUser?.role]);
  const canUseAdminPersonalMessaging =
    Boolean(adminUser?.userId) &&
    !isTaskOnlyAdmin &&
    resolveAdminPermission({
      permissionKey: 'NAV_PERSONAL_CHAT',
      ...adminPermissionOptions,
    });
  const canUseAdminGroupMessaging =
    Boolean(adminUser?.userId) &&
    !isTaskOnlyAdmin &&
    resolveAdminPermission({
      permissionKey: 'NAV_GROUP_CHAT',
      ...adminPermissionOptions,
    });
  const canUseAdminEventMessaging =
    Boolean(adminUser?.userId) &&
    !isTaskOnlyAdmin &&
    resolveAdminPermission({
      permissionKey: 'NAV_EVENT_CHAT',
      ...adminPermissionOptions,
    });
  const canViewConcierge = Boolean(adminUser?.userId) && (
    resolveAdminPermission({ permissionKey: 'CAN_MANAGE_CONCIERGE', ...adminPermissionOptions }) ||
    resolveAdminPermission({ permissionKey: 'NAV_CONCIERGE', ...adminPermissionOptions })
  );
  const { data: conciergePendingCount, refresh: refreshConciergePendingCount } = useBadgeQuery({
    queryKey: ['admin-concierge-pending-count'],
    enabled: canViewConcierge && !checking, visible: canViewConcierge, initialValue: 0,
    queryFn: async () => {
      const data = await getAdminConciergePendingCount();
      if (!Number.isSafeInteger(data?.pendingCount) || data.pendingCount < 0) throw new Error('Invalid concierge count');
      return data.pendingCount;
    },
  });
  const canUseAnyAdminMessaging =
    canUseAdminPersonalMessaging ||
    canUseAdminGroupMessaging ||
    canUseAdminEventMessaging;
  const adminMessagingDeniedRef = useRef({
    personal: false,
    group: false,
    event: false,
  });

  const canUseAdminMessagingPermissionForSession = useCallback(
    (sessionUser, permissionKey) =>
      Boolean(sessionUser?.userId) &&
      String(sessionUser?.role || '').toLowerCase() !== 'task_only' &&
      resolveAdminPermission({
        role: sessionUser?.role,
        permissions: sessionUser?.permissions,
        isExternalAccount: sessionUser?.is_external_account === true,
        permissionKey,
      }),
    []
  );

  const recoverAdminMessagingPermission = useCallback(async (channel) => {
    const isCurrent = beginRequest(`permission-${channel}`);
    if (!isCurrent()) return false;
    const permissionKeyByChannel = {
      personal: 'NAV_PERSONAL_CHAT',
      group: 'NAV_GROUP_CHAT',
      event: 'NAV_EVENT_CHAT',
    };
    const permissionKey = permissionKeyByChannel[channel];
    const refreshed = await fetchSession?.().catch(() => null);
    if (!isCurrent()) return false;
    const stillAllowed = canUseAdminMessagingPermissionForSession(refreshed, permissionKey);
    adminMessagingDeniedRef.current[channel] = !stillAllowed;
    return stillAllowed;
  }, [badgeScope, canUseAdminMessagingPermissionForSession, fetchSession]);

  const socketRef      = useRef(null);
  const joinedRoomsRef = useRef(new Set());
  const refreshLockRef = useRef(false);
  const refreshPendingRef = useRef(false);
  const eventRefreshTimerRef = useRef(null);
  const adminPersonalRefreshTimerRef = useRef(null);
  const artistPersonalRefreshTimerRef = useRef(null);
  const groupRefreshTimerRef = useRef(null);
  const pollTimerRef   = useRef(null);
  const adminEventReadOverrideRef = useRef(new Map());
  const artistEventReadOverrideRef = useRef(new Map());
  const adminGroupReadOverrideRef = useRef(new Map());
  const artistGroupReadOverrideRef = useRef(new Map());
  const adminPersonalUnreadCapRef = useRef({ count: null, expiresAt: 0 });

  useEffect(() => {
    refreshLockRef.current = false;
    refreshPendingRef.current = false;
    joinedRoomsRef.current.clear();
    [adminEventReadOverrideRef, artistEventReadOverrideRef, adminGroupReadOverrideRef, artistGroupReadOverrideRef]
      .forEach((ref) => ref.current.clear());
    adminPersonalUnreadCapRef.current = { count: null, expiresAt: 0 };
    lastAdminCountRef.current = 0;
  }, [badgeScope]);

  /* ====== 「一瞬0になる」揺らぎを抑えるためのガード ====== */
  const lastAdminCountRef   = useRef(0);
  const verifyZeroTimerRef  = useRef(null);

  const setAdminCountSafely = useCallback((n) => {
    // そのまま更新
    setAdminMessageCount(n);
    lastAdminCountRef.current = n;
  }, [badgeScope]);

  const optimisticallyClearAdminPersonalUnread = useCallback((clearedUnreadCount = 0) => {
    const cleared = Math.max(0, Number(clearedUnreadCount || 0));
    if (cleared <= 0) return;
    const nextCount = Math.max(0, Number(lastAdminCountRef.current || 0) - cleared);
    adminPersonalUnreadCapRef.current = {
      count: nextCount,
      expiresAt: Date.now() + UNREAD_OVERRIDE_TTL_MS,
    };
    setAdminCountSafely(nextCount);
  }, [badgeScope, setAdminCountSafely]);

  const verifyAndSetAdminZero = useCallback(async () => {
    const isCurrent = beginRequest('admin-personal');
    if (!isCurrent()) return;
    if (!canUseAdminPersonalMessaging) return;
    try {
      const data = await getAdminPersonalUnreadCount();
      if (!isCurrent()) return;
      if (!Number.isSafeInteger(data?.unreadCount) || data.unreadCount < 0) throw new Error('Invalid unread count');
      const n = Number(data?.unreadCount || 0);
      setAdminCountSafely(n);
      if (data?.tone === 'alert' || data?.tone === 'ok') {
        setAdminArtistChatTone(data.tone);
      } else if (n === 0) {
        setAdminArtistChatTone(null);
      }
    } catch (e) {
      // 失敗時は現状維持（0に落とさない）
      console.error('[admin unread] verify failed:', e);
    }
  }, [badgeScope, canUseAdminPersonalMessaging, setAdminArtistChatTone, setAdminCountSafely]);

  /* URL から現在の役割を推定 */
  const activeRole = useMemo(() => {
    const p = pathname;
    if (p.startsWith('/artist')) return C.ARTIST;
    if (p.startsWith('/admin'))  return C.ADMIN;
    if (adminUser?.userId)  return C.ADMIN;
    if (artistUser?.userId) return C.ARTIST;
    return null;
  }, [badgeScope, pathname, adminUser, artistUser]);

  /* イベントチャットの表示用合計（マップの合計を常に使用） */
  const eventChatMessageCount = useMemo(() => {
    const map =
      activeRole === C.ADMIN  ? eventUnreadMapAdmin :
      activeRole === C.ARTIST ? eventUnreadMapArtist : {};
    return Object.values(map).reduce((s, v) => s + Number(v || 0), 0);
  }, [badgeScope, activeRole, eventUnreadMapAdmin, eventUnreadMapArtist]);

  /* 互換：合計を直接セットする旧 API。内部的には役割側の合計だけ更新（表示は上の派生値）。 */
  const setEventChatMessageCount = useCallback((val) => {
    if (activeRole === C.ADMIN)  setEventChatUnreadAdmin(Number(val || 0));
    if (activeRole === C.ARTIST) setEventChatUnreadArtist(Number(val || 0));
  }, [badgeScope, activeRole]);

  /* ========= 個人メッセージ未読：アーティスト側 ========= */
  const refreshArtistPersonalUnread = useCallback(async () => {
    const isCurrent = beginRequest('artist-personal');
    if (!isCurrent()) return;
    if (!artistUser?.userId) return;
    try {
      const { unreadCount } = await getArtistPersonalUnreadCount();
      if (!isCurrent()) return;
      if (!Number.isSafeInteger(unreadCount) || unreadCount < 0) throw new Error('Invalid unread count');
      setArtistPersonalMessageCount(Number(unreadCount || 0));
    } catch (e) {
      console.error('[artist personal unread] fetch failed:', e);
    }
  }, [badgeScope, artistUser]);

  /* ========= グループチャット未読：アーティスト側 ========= */
  const refreshArtistGroupChatUnread = useCallback(async () => {
    const isCurrent = beginRequest('artist-group');
    if (!isCurrent()) return;
    if (!artistUser?.userId) return;
    try {
      const { unreadCounts } = await getArtistGroupChatUnreadCount();
      if (!isCurrent()) return;
      assertUnreadRows(unreadCounts, 'thread_id');
      const map = {};
      if (Array.isArray(unreadCounts)) {
        unreadCounts.forEach(({ thread_id, unread_count }) => {
          if (!thread_id) return;
          map[thread_id] = Number(unread_count || 0);
        });
      }
      const effectiveMap = applyUnreadOverrideMap(map, artistGroupReadOverrideRef);
      setArtistGroupUnreadMap(effectiveMap);
      setArtistGroupChatMessageCount(sumUnreadMap(effectiveMap));
    } catch (e) {
      console.error('[artist group unread] fetch failed:', e);
    }
  }, [badgeScope, artistUser]);

  /* ========= グループチャット未読：管理者側 ========= */
  const refreshAdminGroupChatUnread = useCallback(async () => {
    const isCurrent = beginRequest('admin-group');
    if (!isCurrent()) return;
    if (checking) return;
    if (!canUseAdminGroupMessaging || adminMessagingDeniedRef.current.group) {
      setAdminGroupUnreadMap({});
      setAdminGroupChatMessageCount(0);
      setAdminGroupChatTone(null);
      return;
    }
    try {
      const { unreadCounts, tone } = await getAdminGroupChatUnreadCount();
      if (!isCurrent()) return;
      assertUnreadRows(unreadCounts, 'thread_id');
      const map = {};
      let hasAssignedUnread = false;
      if (Array.isArray(unreadCounts)) {
        unreadCounts.forEach(({ thread_id, unread_count, is_assigned_to_me }) => {
          if (!thread_id) return;
          const nextUnread = Number(unread_count || 0);
          map[thread_id] = nextUnread;
          if (nextUnread > 0 && Boolean(is_assigned_to_me)) {
            hasAssignedUnread = true;
          }
        });
      }
      const effectiveMap = applyUnreadOverrideMap(map, adminGroupReadOverrideRef);
      const total = sumUnreadMap(effectiveMap);
      const effectiveAssignedUnread = Array.isArray(unreadCounts)
        ? unreadCounts.some(
            ({ thread_id, is_assigned_to_me }) =>
              Boolean(is_assigned_to_me) && Number(effectiveMap?.[thread_id] || 0) > 0,
          )
        : false;
      setAdminGroupUnreadMap(effectiveMap);
      setAdminGroupChatMessageCount(total);
      if (tone === 'alert' || tone === 'ok') {
        setAdminGroupChatTone(tone);
      } else if (total > 0) {
        setAdminGroupChatTone((effectiveAssignedUnread || hasAssignedUnread) ? 'alert' : 'ok');
      } else {
        setAdminGroupChatTone(null);
      }
    } catch (e) {
      if (!isCurrent()) return;
      if (Number(e?.status) === 403) {
        const stillAllowed = await recoverAdminMessagingPermission('group');
        if (!isCurrent()) return;
        if (!stillAllowed) {
          setAdminGroupUnreadMap({});
          setAdminGroupChatMessageCount(0);
          setAdminGroupChatTone(null);
        }
        return;
      }
      console.error('[admin group unread] fetch failed:', e);
    }
  }, [badgeScope, canUseAdminGroupMessaging, checking, recoverAdminMessagingPermission]);

  /* ========= 個人メッセージ未読：管理者側（★追加） ========= */
  const refreshAdminPersonalUnread = useCallback(async (opts = {}) => {
    const isCurrent = beginRequest('admin-personal');
    if (!isCurrent()) return;
    if (checking) return;
    if (!canUseAdminPersonalMessaging || adminMessagingDeniedRef.current.personal) {
      setAdminCountSafely(0);
      setAdminArtistChatTone(null);
      return;
    }
    const { verifyIfZero = false } = opts;
    try {
      const data = await getAdminPersonalUnreadCount();
      if (!isCurrent()) return;
      if (!Number.isSafeInteger(data?.unreadCount) || data.unreadCount < 0) throw new Error('Invalid unread count');
      const n = Number(data?.unreadCount || 0);
      const rawTone = data?.tone;
      const now = Date.now();
      const optimisticCap = adminPersonalUnreadCapRef.current;
      const hasOptimisticCap =
        Number.isFinite(Number(optimisticCap?.count)) &&
        Number(optimisticCap?.expiresAt || 0) > now;
      const resolvedCount =
        hasOptimisticCap && n > Number(optimisticCap.count)
          ? Number(optimisticCap.count)
          : n;

      if (verifyIfZero && resolvedCount === 0 && lastAdminCountRef.current > 0) {
        // 一時0の揺らぎ対策：短い遅延後にもう一度だけ確認
        if (verifyZeroTimerRef.current) clearTimeout(verifyZeroTimerRef.current);
        verifyZeroTimerRef.current = setTimeout(() => { verifyAndSetAdminZero(); }, 350);
        return; // ここでは即0に落とさない
      }

      if (!hasOptimisticCap || n <= Number(optimisticCap.count)) {
        adminPersonalUnreadCapRef.current = { count: null, expiresAt: 0 };
      }

      setAdminCountSafely(resolvedCount);
      if (rawTone === 'alert' || rawTone === 'ok') {
        setAdminArtistChatTone(rawTone);
      } else if (resolvedCount === 0) {
        setAdminArtistChatTone(null);
      }
    } catch (e) {
      if (!isCurrent()) return;
      if (Number(e?.status) === 403) {
        const stillAllowed = await recoverAdminMessagingPermission('personal');
        if (!isCurrent()) return;
        if (!stillAllowed) {
          setAdminCountSafely(0);
          setAdminArtistChatTone(null);
        }
        return;
      }
      console.error('[admin personal unread] fetch failed:', e);
      // 失敗時は現状維持
    }
  }, [badgeScope,
    canUseAdminPersonalMessaging,
    checking,
    recoverAdminMessagingPermission,
    setAdminArtistChatTone,
    setAdminCountSafely,
    verifyAndSetAdminZero,
  ]);

  /* ========= イベントチャット未読 ========= */
  const refreshEventChatUnread = useCallback(async () => {
    if (refreshLockRef.current) {
      refreshPendingRef.current = true;
      return;
    }
    const isCurrent = beginRequest('events');
    if (!isCurrent()) return;
    refreshLockRef.current = true;
    try {
      const jobs = [];
      const ensureJoined = (eventIds = []) => {
        const sock = socketRef.current;
        if (!sock) return;
        eventIds.forEach((evId) => {
          const room = `event_${evId}`;
          if (!joinedRoomsRef.current.has(room)) {
            try { sock.emit('join_event', evId); joinedRoomsRef.current.add(room); } catch (e) {
              console.error('[join_event] failed:', e);
            }
          }
        });
      };
      if (canUseAdminEventMessaging && !adminMessagingDeniedRef.current.event) {
        jobs.push(
          (async () => {
            try {
              const { unreadCounts, tone } = await getAdminEventChatUnreadCounts();
              if (!isCurrent()) return;
              assertUnreadRows(unreadCounts, 'event_id');
              const map = {};
              const evs = [];
              unreadCounts.forEach(({ event_id, unread_count }) => {
                map[event_id] = Number(unread_count || 0);
                evs.push(event_id);
              });
              const effectiveMap = applyUnreadOverrideMap(map, adminEventReadOverrideRef);
              const sum = sumUnreadMap(effectiveMap);
              setEventUnreadMapAdmin(effectiveMap);
              setEventChatUnreadAdmin(sum);
              if (tone === 'alert' || tone === 'ok') {
                setAdminEventChatTone(tone);
              } else if (sum === 0) {
                setAdminEventChatTone(null);
              }
              ensureJoined(evs);
            } catch (e) {
              if (!isCurrent()) return;
              if (Number(e?.status) === 403) {
                const stillAllowed = await recoverAdminMessagingPermission('event');
                if (!isCurrent()) return;
                if (!stillAllowed) {
                  adminMessagingDeniedRef.current.event = true;
                }
                return;
              }
              console.error('[unread admin] fetch failed:', e);
            }
          })()
        );
      } else {
        setEventUnreadMapAdmin({});
        setEventChatUnreadAdmin(0);
        setAdminEventChatTone(null);
      }
      if (artistUser?.userId) {
        jobs.push(
          (async () => {
            try {
              const { unreadCounts } = await getArtistEventChatUnreadCounts();
              if (!isCurrent()) return;
              assertUnreadRows(unreadCounts, 'event_id');
              const map = {};
              const evs = [];
              unreadCounts.forEach(({ event_id, unread_count }) => {
                map[event_id] = Number(unread_count || 0);
                evs.push(event_id);
              });
              const effectiveMap = applyUnreadOverrideMap(map, artistEventReadOverrideRef);
              const sum = sumUnreadMap(effectiveMap);
              setEventUnreadMapArtist(effectiveMap);
              setEventChatUnreadArtist(sum);
              ensureJoined(evs);
            } catch (e) {
              console.error('[unread artist] fetch failed:', e);
            }
          })()
        );
      }
      await Promise.all(jobs);
    } finally {
      if (!isCurrent()) return;
      refreshLockRef.current = false;
      if (refreshPendingRef.current) {
        refreshPendingRef.current = false;
        refreshEventChatUnread();
      }
    }
  }, [badgeScope, artistUser, canUseAdminEventMessaging, recoverAdminMessagingPermission]);

  /* ========= 未読メンション在否（event/group）＝バッジ黄色判定 ========= */
  const refreshMentionSummary = useCallback(async () => {
    const isCurrent = beginRequest('mentions');
    if (!isCurrent()) return;
    if (checking) return;
    if (!canUseAnyAdminMessaging && !artistUser?.userId) return;
    try {
      const { event, group } = await getChatMentionUnreadSummary();
      if (!isCurrent()) return;
      if (typeof event !== 'boolean' || typeof group !== 'boolean') throw new Error('Invalid mention summary');
      setEventChatHasMention(event);
      setGroupChatHasMention(Boolean(group));
    } catch (e) {
      // 非致命：失敗時は現状維持（バッジは通常色のまま）。
    }
  }, [badgeScope, pathname, checking, canUseAnyAdminMessaging, artistUser]);

  /* —— アーティスト：全イベント room 参加 —— */
  const joinAllArtistEventRooms = useCallback(async (sock) => {
    const isCurrent = beginRequest('join-artist');
    if (!isCurrent()) return;
    try {
      let offset = 0;
      for (let i = 0; i < 1000; i++) {
        const { events = [] } = await listArtistEventChatAvailableEvents(offset);
        if (!isCurrent()) return;
        if (!events.length) break;
        events.forEach(e => {
          const room = `event_${e.event_uuid}`;
          if (!joinedRoomsRef.current.has(room)) {
            sock.emit('join_event', e.event_uuid);
            joinedRoomsRef.current.add(room);
          }
        });
        offset += events.length;
      }
    } catch (e) {
      console.error('[join rooms] artist failed:', e);
    } finally {
      refreshEventChatUnread();
    }
  }, [badgeScope, refreshEventChatUnread]);

  /* —— 管理者：admins 参加 —— */
  const joinAdminsRoom = useCallback((sock) => {
    sock.emit('join_admins');
    if (canUseAdminPersonalMessaging) {
      sock.emit('join_admin_user', adminUser.userId);
    }
    refreshEventChatUnread();
  }, [badgeScope, refreshEventChatUnread, adminUser, canUseAdminPersonalMessaging]);

  /* ========= ソケット初期化 ========= */
  useEffect(() => {
    adminMessagingDeniedRef.current = {
      personal: false,
      group: false,
      event: false,
    };
  }, [badgeScope,
    adminUser?.userId,
    canUseAdminPersonalMessaging,
    canUseAdminGroupMessaging,
    canUseAdminEventMessaging,
  ]);

  useEffect(() => {
    if (checking) return undefined;
    if (!canUseAnyAdminMessaging && !artistUser?.userId) return; // 未ログイン時は接続しない

    socketRef.current = getSocket();
    const sock = socketRef.current;
    if (!sock.connected) {
      try { sock.connect(); } catch (e) { console.error('[socket] connect error:', e); }
    }

    const onConnect = () => {
      if (canUseAnyAdminMessaging)  {
        joinAdminsRoom(sock);
      }
      refreshAdminPersonalUnread(); // ★管理者の個別未読を初回取得
      refreshAdminGroupChatUnread();
      if (artistUser?.userId) {
        joinedRoomsRef.current.clear();
        joinAllArtistEventRooms(sock);

        // 個別メッセージ用：自分専用 room に参加（サーバ側で join_artist に対応させる）
        try { sock.emit('join_artist', artistUser.userId); } catch {}
        refreshArtistPersonalUnread();
      }
    };

    sock.on('connect', onConnect);

    /* —— §3.7: チャンネル別 unread-count 再取得は 250ms デバウンス（バースト吸収） —— */
    const scheduleEventRefresh = () => {
      if (eventRefreshTimerRef.current) return;
      eventRefreshTimerRef.current = setTimeout(() => {
        eventRefreshTimerRef.current = null;
        refreshEventChatUnread();
        refreshMentionSummary();
      }, BADGE_REFRESH_DEBOUNCE_MS);
    };
    const scheduleAdminPersonalRefresh = () => {
      if (adminPersonalRefreshTimerRef.current) return;
      adminPersonalRefreshTimerRef.current = setTimeout(() => {
        adminPersonalRefreshTimerRef.current = null;
        refreshAdminPersonalUnread({ verifyIfZero: true });
      }, BADGE_REFRESH_DEBOUNCE_MS);
    };
    const scheduleArtistPersonalRefresh = () => {
      if (artistPersonalRefreshTimerRef.current) return;
      artistPersonalRefreshTimerRef.current = setTimeout(() => {
        artistPersonalRefreshTimerRef.current = null;
        refreshArtistPersonalUnread();
      }, BADGE_REFRESH_DEBOUNCE_MS);
    };
    const scheduleGroupRefresh = () => {
      if (groupRefreshTimerRef.current) return;
      groupRefreshTimerRef.current = setTimeout(() => {
        groupRefreshTimerRef.current = null;
        if (artistUser?.userId) {
          refreshArtistGroupChatUnread();
        }
        refreshAdminGroupChatUnread();
        refreshMentionSummary();
      }, BADGE_REFRESH_DEBOUNCE_MS);
    };

    const onNewEventMessage = (payload = {}) => {
      const isSelfArtist =
        activeRole === C.ARTIST &&
        payload.senderType === 'artist' &&
        payload.senderId != null &&
        String(payload.senderId) === String(artistUser?.userId);
      const isSelfAdmin =
        activeRole === C.ADMIN &&
        payload.senderType === 'admin' &&
        payload.senderId != null &&
        String(payload.senderId) === String(adminUser?.userId);
      if (isSelfArtist || isSelfAdmin) {
        // 自分が送信した場合は未読リフレッシュ不要
        return;
      }
      if (isPayloadForActiveChat('event', payload)) {
        return;
      }
      scheduleEventRefresh();
    };
    const onReadUpdated     = () => scheduleEventRefresh();
    sock.on(SOCKET_EVENTS.NEW_EVENT_MESSAGE, onNewEventMessage);
    sock.on(SOCKET_EVENTS.READ_UPDATED,      onReadUpdated);

    /* —— 個別メッセージ：新着/既読更新トリガ —— */
    const onNewPersonalMessageForArtist = (payload = {}) => {
      const artistId = payload?.artistId || payload?.artist_id || null;
      if (String(artistId) === String(artistUser?.userId)) {
        if (isPayloadForActiveChat('personal', payload)) return;
        scheduleArtistPersonalRefresh();
      }
    };
    const onGroupChatChanged = () => {
      // §3.7: 250ms デバウンスでチャンネル unread-count 再取得（ペイロード加算しない）。
      scheduleGroupRefresh();
    };
    const onNewGroupChatMessage = (payload = {}) => {
      const isActiveGroupChat = isPayloadForActiveChat('group', payload);
      if (!isActiveGroupChat) {
        onGroupChatChanged();
      }
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      if (isActiveGroupChat || isAnyChatOpenPath(path)) return;

      const senderType = String(payload?.senderType || payload?.sender_type || '').toLowerCase();
      const senderId = payload?.senderId || payload?.sender_id || null;
      const isSelfArtist =
        activeRole === C.ARTIST &&
        senderType === 'artist' &&
        senderId != null &&
        String(senderId) === String(artistUser?.userId);
      const isSelfAdmin =
        activeRole === C.ADMIN &&
        senderType === 'admin' &&
        senderId != null &&
        String(senderId) === String(adminUser?.userId);
      if (isSelfArtist || isSelfAdmin) return;

      const threadTitle = String(
        payload?.threadTitle || payload?.thread_title || payload?.title || ''
      ).trim();
      dispatchPeerNotification({
        id: payload?.messageId ? `group-message:${payload.messageId}` : undefined,
        type: 'chat.message',
        title: 'グループチャットの新着',
        summary: threadTitle || 'グループチャットに新着があります',
        targetUrl: activeRole === C.ADMIN ? '/admin/workspaces/communications/group-chat' : '/artist/workspaces/communications/chat',
        audience: activeRole || undefined,
        icon: 'chat',
        color: 'var(--green-500)',
      });
    };
    // 管理者側は誰宛でも合計を取り直す（0チラつき対策で verify 付き）
    const onAnyPersonalChangedForAdmin = (payload = {}) => {
      if (!canUseAdminPersonalMessaging) return;
      // adminUserId が無い場合は従来どおり全件でリフレッシュ、ある場合は自分宛のみ
      if (!payload.adminUserId || String(payload.adminUserId) === String(adminUser.userId)) {
        if (isPayloadForActiveChat('personal', payload)) return;
        // §3.7: 250ms デバウンス。verify 付き取得は 0チラつきガードを維持。
        scheduleAdminPersonalRefresh();
      }
    };

    sock.on(SOCKET_EVENTS.NEW_MESSAGE, onNewPersonalMessageForArtist);
    sock.on(SOCKET_EVENTS.NEW_MESSAGE, onAnyPersonalChangedForAdmin); // 同じイベントに複数ハンドラOK
    sock.on(SOCKET_EVENTS.MESSAGES_READ, onAnyPersonalChangedForAdmin);
    sock.on(SOCKET_EVENTS.NEW_GROUP_CHAT_MESSAGE, onNewGroupChatMessage);
    sock.on(SOCKET_EVENTS.GROUP_CHAT_READ_UPDATED, onGroupChatChanged);
    sock.on(SOCKET_EVENTS.GROUP_CHAT_SETTINGS_UPDATED, onGroupChatChanged);
    sock.on(SOCKET_EVENTS.GROUP_CHAT_MEMBERSHIP_CHANGED, onGroupChatChanged);
    sock.on(SOCKET_EVENTS.GROUP_CHAT_MESSAGE_DELETED, onGroupChatChanged);

    if (sock.connected) onConnect();

    return () => {
      sock.off('connect', onConnect);
      sock.off(SOCKET_EVENTS.NEW_EVENT_MESSAGE, onNewEventMessage);
      sock.off(SOCKET_EVENTS.READ_UPDATED,      onReadUpdated);
      sock.off(SOCKET_EVENTS.NEW_MESSAGE,       onNewPersonalMessageForArtist);
      sock.off(SOCKET_EVENTS.NEW_MESSAGE,       onAnyPersonalChangedForAdmin);
      sock.off(SOCKET_EVENTS.MESSAGES_READ,     onAnyPersonalChangedForAdmin);
      sock.off(SOCKET_EVENTS.NEW_GROUP_CHAT_MESSAGE, onNewGroupChatMessage);
      sock.off(SOCKET_EVENTS.GROUP_CHAT_READ_UPDATED, onGroupChatChanged);
      sock.off(SOCKET_EVENTS.GROUP_CHAT_SETTINGS_UPDATED, onGroupChatChanged);
      sock.off(SOCKET_EVENTS.GROUP_CHAT_MEMBERSHIP_CHANGED, onGroupChatChanged);
      sock.off(SOCKET_EVENTS.GROUP_CHAT_MESSAGE_DELETED, onGroupChatChanged);
      if (eventRefreshTimerRef.current) {
        clearTimeout(eventRefreshTimerRef.current);
        eventRefreshTimerRef.current = null;
      }
      if (adminPersonalRefreshTimerRef.current) {
        clearTimeout(adminPersonalRefreshTimerRef.current);
        adminPersonalRefreshTimerRef.current = null;
      }
      if (artistPersonalRefreshTimerRef.current) {
        clearTimeout(artistPersonalRefreshTimerRef.current);
        artistPersonalRefreshTimerRef.current = null;
      }
      if (groupRefreshTimerRef.current) {
        clearTimeout(groupRefreshTimerRef.current);
        groupRefreshTimerRef.current = null;
      }
    };
  }, [badgeScope,
    activeRole,
    adminUser, artistUser,
    checking,
    canUseAnyAdminMessaging,
    canUseAdminPersonalMessaging,
    joinAdminsRoom, joinAllArtistEventRooms,
    refreshEventChatUnread,
    refreshArtistPersonalUnread,
    refreshArtistGroupChatUnread,
    refreshAdminGroupChatUnread,
    refreshAdminPersonalUnread,
    refreshMentionSummary
  ]);

  // Socket is shared with timetable presence and other realtime features, so a
  // user without chat permissions must not disconnect it. Reset only when the
  // session explicitly logs out.
  useEffect(() => {
    const handleLogout = () => {
      resetSocket();
      joinedRoomsRef.current.clear();
      refreshLockRef.current = false;
    };
    window.addEventListener('casticart:logout', handleLogout);
    return () => window.removeEventListener('casticart:logout', handleLogout);
  }, [badgeScope]);

  useEffect(() => {
    const refreshTopicRead = ({ detail }) => {
      if (detail?.scope === 'event') refreshEventChatUnread();
      else if (detail?.scope === 'group') {
        if (detail.role === 'artist') refreshArtistGroupChatUnread();
        else refreshAdminGroupChatUnread();
      } else if (detail?.role === 'artist') refreshArtistPersonalUnread();
      else refreshAdminPersonalUnread();
    };
    window.addEventListener('chat-topics-read', refreshTopicRead);
    return () => window.removeEventListener('chat-topics-read', refreshTopicRead);
  }, [refreshEventChatUnread, refreshArtistGroupChatUnread, refreshAdminGroupChatUnread, refreshArtistPersonalUnread, refreshAdminPersonalUnread]);

  /* 初回フェッチ＆フォールバック */
  useEffect(() => {
    if (checking) return undefined;
    if (!canUseAnyAdminMessaging && !artistUser?.userId) return;
    refreshEventChatUnread();
    refreshArtistPersonalUnread();
    refreshArtistGroupChatUnread();
    refreshAdminGroupChatUnread();
    refreshAdminPersonalUnread();
    refreshMentionSummary();

    const onVis = () => {
      if (document.visibilityState === 'visible') {
        refreshArtistPersonalUnread();
        refreshArtistGroupChatUnread();
        refreshAdminGroupChatUnread();
        refreshAdminPersonalUnread({ verifyIfZero: true });
        refreshEventChatUnread();
        refreshMentionSummary();
      }
    };
    document.addEventListener('visibilitychange', onVis);

    // 軽めのフォールバックポーリング
    pollTimerRef.current = setInterval(() => {
      refreshArtistPersonalUnread();
      refreshArtistGroupChatUnread();
      refreshAdminGroupChatUnread();
      refreshAdminPersonalUnread();
      refreshEventChatUnread();
      refreshMentionSummary();
    }, 30000);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (verifyZeroTimerRef.current) clearTimeout(verifyZeroTimerRef.current);
    };
  }, [badgeScope,
    checking,
    canUseAnyAdminMessaging,
    artistUser,
    refreshEventChatUnread,
    refreshArtistPersonalUnread,
    refreshArtistGroupChatUnread,
    refreshAdminGroupChatUnread,
    refreshAdminPersonalUnread,
    refreshMentionSummary
  ]);

  /* 通信復帰（offline→online）時の軽い未読再取得。エッジ検出は
     ConnectivityContext に集約されており、ここでは restored イベントを購読して
     既存の再取得関数を1回呼ぶだけ（全画面リフレッシュはしない）。 */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (checking) return undefined;
    if (!canUseAnyAdminMessaging && !artistUser?.userId) return undefined;
    const onRestored = () => {
      refreshArtistPersonalUnread();
      refreshArtistGroupChatUnread();
      refreshAdminGroupChatUnread();
      refreshAdminPersonalUnread();
      refreshEventChatUnread();
      refreshMentionSummary();
    };
    window.addEventListener('casticart:connectivity-restored', onRestored);
    return () => window.removeEventListener('casticart:connectivity-restored', onRestored);
  }, [badgeScope,
    checking,
    canUseAnyAdminMessaging,
    artistUser,
    refreshEventChatUnread,
    refreshArtistPersonalUnread,
    refreshArtistGroupChatUnread,
    refreshAdminGroupChatUnread,
    refreshAdminPersonalUnread,
    refreshMentionSummary,
  ]);

  /* メッセージ画面で既読にした直後に未読を引かせるためのフック（イベント単位） */
  const markEventAsRead = useCallback((eventId) => {
    if (!eventId) return;
    if (activeRole === C.ADMIN) {
      adminEventReadOverrideRef.current.set(String(eventId), Date.now() + UNREAD_OVERRIDE_TTL_MS);
      setEventUnreadMapAdmin(prev => {
        if (!prev || prev[eventId] == null) return prev;
        const next = { ...prev, [eventId]: 0 };
        const sum  = sumUnreadMap(next);
        setEventChatUnreadAdmin(sum);
        return next;
      });
    } else if (activeRole === C.ARTIST) {
      artistEventReadOverrideRef.current.set(String(eventId), Date.now() + UNREAD_OVERRIDE_TTL_MS);
      setEventUnreadMapArtist(prev => {
        if (!prev || prev[eventId] == null) return prev;
        const next = { ...prev, [eventId]: 0 };
        const sum  = sumUnreadMap(next);
        setEventChatUnreadArtist(sum);
        return next;
      });
    }
  }, [badgeScope, activeRole]);

  const markAdminGroupThreadAsRead = useCallback((threadId) => {
    if (!threadId) return;
    adminGroupReadOverrideRef.current.set(String(threadId), Date.now() + UNREAD_OVERRIDE_TTL_MS);
    setAdminGroupUnreadMap((prev) => {
      if (!prev || prev[threadId] == null) return prev;
      const next = { ...prev, [threadId]: 0 };
      const total = sumUnreadMap(next);
      setAdminGroupChatMessageCount(total);
      if (total === 0) setAdminGroupChatTone(null);
      return next;
    });
  }, [badgeScope]);

  const markArtistGroupThreadAsRead = useCallback((threadId) => {
    if (!threadId) return;
    artistGroupReadOverrideRef.current.set(String(threadId), Date.now() + UNREAD_OVERRIDE_TTL_MS);
    setArtistGroupUnreadMap((prev) => {
      if (!prev || prev[threadId] == null) return prev;
      const next = { ...prev, [threadId]: 0 };
      setArtistGroupChatMessageCount(sumUnreadMap(next));
      return next;
    });
  }, [badgeScope]);

  /* イベントごとの未読数を取得（使う側が便利なように公開） */
  const getEventUnreadCount = useCallback((eventId) => {
    if (!eventId) return 0;
    const map =
      activeRole === C.ADMIN  ? eventUnreadMapAdmin :
      activeRole === C.ARTIST ? eventUnreadMapArtist : {};
    return Number(map?.[eventId] || 0);
  }, [badgeScope, activeRole, eventUnreadMapAdmin, eventUnreadMapArtist]);

  return (
    <MessageCountContext.Provider value={{
      // 個別メッセージ（合計）
      artistMessageCount, setArtistMessageCount,
      artistPersonalMessageCount,
      artistGroupChatMessageCount,
      artistGroupUnreadMap,
      adminGroupChatMessageCount,
      adminGroupUnreadMap,
      adminMessageCount,  setAdminMessageCount,

      // イベントチャット（合計バッジ）
      eventChatMessageCount,
      setEventChatMessageCount,

      // イベントチャット（役割別の実値＆マップ）
      eventChatUnreadAdmin,
      eventChatUnreadArtist,
      eventUnreadMapAdmin,
      eventUnreadMapArtist,

      // 管理者バッジトーン
      adminArtistChatTone,
      setAdminArtistChatTone,
      adminEventChatTone,
      setAdminEventChatTone,
      adminGroupChatTone,
      setAdminGroupChatTone,

      // 未読メンション在否（event/group）＝バッジ黄色（メンション色）判定
      eventChatHasMention,
      groupChatHasMention,
      refreshMentionSummary,

      // コンシェルジュ（管理者）未処理件数
      conciergePendingCount,
      refreshConciergePendingCount,

      // 取得・更新系
      refreshEventChatUnread,
      getEventUnreadCount,
      markEventAsRead,
      markAdminGroupThreadAsRead,
      markArtistGroupThreadAsRead,
      optimisticallyClearAdminPersonalUnread,

      // 追加公開（個人メッセージ）
      refreshArtistPersonalUnread,
      refreshArtistGroupChatUnread,
      refreshAdminGroupChatUnread,
      refreshAdminPersonalUnread, // ★追加公開
    }}>
      {children}
    </MessageCountContext.Provider>
  );
};

export const useMessageCount = () =>
  useContext(MessageCountContext) || defaultMessageCountContext;
